import { Button } from "@openanalyst-ai/ui/button"
import { Checkbox } from "@openanalyst-ai/ui/checkbox"
import { useDialog } from "@openanalyst-ai/ui/context/dialog"
import { Dialog } from "@openanalyst-ai/ui/dialog"
import { Icon } from "@openanalyst-ai/ui/icon"
import { TextField } from "@openanalyst-ai/ui/text-field"
import { isPlainProjectFolderName } from "@openanalyst-ai/core/util/project-folder-name"
import { For, Show, createResource, createSignal } from "solid-js"
import { allowAppIDsFor } from "@/components/dialog-custom-template-selection"
import { useLanguage } from "@/context/language"
import { usePlatform } from "@/context/platform"

export type CustomTemplateSelection = {
  name: string
  rootDirectory?: string
  allowAppIDs: string[]
}

/**
 * Name + location + which bundled apps the new project starts with.
 *
 * The app checkboxes are not decoration: the selection is written to the project's
 * `.openanalyst/plugins/.bindings.json` as an `allow` list, which the engine's plugin
 * discovery already hard-gates auto-materialization on (see readPluginBindings in
 * packages/openanalyst/src/plugin/folder-discovery.ts — the shipped starter templates use
 * the same file). Nothing is removed permanently: an app left unchecked can still be
 * installed into the project by hand later.
 *
 * NOTE ON STATE: this tracks what the user UNCHECKED, not what is checked. Everything
 * starts on, so "untouched" is simply an empty set — which means the checkbox state never
 * has to be seeded from the loaded app list, and nothing writes a signal from inside the
 * resource fetcher. An earlier version seeded a "checked" set that way and the list stopped
 * repainting after the first toggle.
 */
export function DialogCustomTemplate(props: {
  initialName: string
  rootLabel: string
  /** Guarded location picker owned by the page — it rejects the filesystem root (and
   *  toasts) before handing a directory back. The dialog deliberately does NOT call
   *  openDirectoryPickerDialog itself, or it would skip that guard. */
  onChooseLocation?: () => Promise<string | undefined>
  onSubmit: (selection: CustomTemplateSelection) => Promise<void> | void
}) {
  const dialog = useDialog()
  const language = useLanguage()
  const platform = usePlatform()

  const [name, setName] = createSignal(props.initialName)
  const [root, setRoot] = createSignal<string | undefined>(undefined)
  // Starts EMPTY: every app is unchecked and the user opts in to the ones they want.
  const [selected, setSelected] = createSignal<Set<string>>(new Set())

  const [apps] = createResource(async () => (await platform.listBundledApps?.().catch(() => undefined)) ?? [])

  const total = () => apps()?.length ?? 0
  const isChecked = (id: string) => selected().has(id)
  const selectedCount = () => selected().size
  const allSelected = () => total() > 0 && selected().size >= total()
  const invalidName = () => name().trim().length > 0 && !isPlainProjectFolderName(name())
  // Nothing checked is the STARTING state now, so Create stays disabled until the user
  // picks at least one app.
  const noneSelected = () => selectedCount() === 0

  const toggle = (id: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })

  const toggleAll = () =>
    setSelected(allSelected() ? new Set<string>() : new Set((apps() ?? []).map((app) => app.id)))

  const chooseLocation = async () => {
    const directory = await props.onChooseLocation?.()
    if (directory) setRoot(directory)
  }

  const submit = (event: SubmitEvent) => {
    event.preventDefault()
    if (invalidName() || noneSelected()) return
    const selection = {
      name: name().trim(),
      rootDirectory: root(),
      allowAppIDs: allowAppIDsFor(apps() ?? [], selected()),
    }
    dialog.close()
    queueMicrotask(() => void props.onSubmit(selection))
  }

  return (
    // NOTHING here scrolls, by construction.
    //
    // Dialog's own content element is `overflow: auto`, set by dialog.css as
    // `[data-component="dialog"] [data-slot="dialog-container"] [data-slot="dialog-content"]`
    // — three attribute selectors, so it beats any utility class passed in through `class`
    // and cannot be turned off from here. Any content taller than the dialog therefore
    // scrolls, and an inner scrolling list on top of that gave two scrollbars: scrolling the
    // list chained out to the dialog, and clicking a checkbox scrolled it into view, which
    // dragged the folder-name field and location row off the top with no obvious way back.
    //
    // So the fix is to stop producing overflow at all: `fit` lets the dialog size to its
    // content, and the apps go in a two-column grid that fits without its own scroll area.
    <Dialog
      title={language.t("project.template.title")}
      description={language.t("project.template.description")}
      fit
      transition
      class="w-full max-w-[560px] mx-auto"
    >
      <form data-dialog-custom-template="" onSubmit={submit} class="flex flex-col gap-4 p-6 pt-0">
        <TextField
          autofocus
          type="text"
          label={language.t("project.start.new.name")}
          placeholder={language.t("project.start.new.placeholder")}
          value={name()}
          error={invalidName() ? language.t("project.start.new.nameInvalid") : undefined}
          validationState={invalidName() ? "invalid" : undefined}
          onChange={setName}
        />

        <div class="flex shrink-0 items-center gap-3 rounded-lg border border-border-weak-base bg-surface-secondary-base/40 px-3 py-2.5">
          <Icon name="folder" size="small" />
          <div class="min-w-0 flex-1">
            <div class="text-12-regular text-text-weak">{language.t("project.start.root.label")}</div>
            <div class="truncate text-13-regular text-text-strong" title={root() ?? props.rootLabel}>
              {root() ?? props.rootLabel}
            </div>
          </div>
          <Show when={props.onChooseLocation}>
            <Button type="button" variant="secondary" size="small" class="shrink-0" onClick={() => void chooseLocation()}>
              {language.t("project.start.root.change")}
            </Button>
          </Show>
        </div>

        <div class="flex flex-col gap-2">
          <div class="flex shrink-0 items-center justify-between gap-2">
            <span class="text-13-medium text-text-strong">{language.t("project.template.apps")}</span>
            <Show when={total() > 0}>
              <span class="flex items-center gap-2">
                <span class="text-12-regular text-text-weak">
                  {language.t("project.template.appsHint", {
                    count: String(selectedCount()),
                    total: String(total()),
                  })}
                </span>
                {/* Fixed width: the label flips between two different-length words on every
                    toggle, and letting it resize shifts the whole header row each time. */}
                <Button type="button" variant="ghost" size="small" class="w-[76px] shrink-0" onClick={toggleAll}>
                  {allSelected() ? language.t("project.template.clear") : language.t("project.template.selectAll")}
                </Button>
              </span>
            </Show>
          </div>

          <div class="rounded-lg border border-border-weak-base p-2">
            <Show
              when={total() > 0}
              fallback={
                <div class="flex min-h-[80px] items-center justify-center px-4 text-center text-12-regular text-text-weak">
                  <Show when={!apps.loading}>{language.t("project.template.appsFailed")}</Show>
                </div>
              }
            >
              <div class="grid grid-cols-2 gap-x-2 gap-y-0.5">
                <For each={apps()}>
                  {(app) => (
                    // A plain div, not a <label> — Kobalte's Checkbox renders its own label
                    // around the name, and wrapping that in a second label makes a row click
                    // toggle twice (i.e. do nothing).
                    <div
                      data-custom-template-app={app.id}
                      class="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-secondary-base"
                    >
                      <Checkbox checked={isChecked(app.id)} onChange={(checked) => toggle(app.id, checked)}>
                        {app.name}
                      </Checkbox>
                    </div>
                  )}
                </For>
              </div>
            </Show>
          </div>

          {/* Reserved line so showing it never changes the dialog's height. Weak, not
              critical: with everything unchecked by default this shows immediately, and red
              text on a dialog the user has not touched yet reads as an error they caused. */}
          <div class="min-h-[16px] shrink-0 text-12-regular text-text-weak">
            <Show when={noneSelected()}>{language.t("project.template.appsEmpty")}</Show>
          </div>
        </div>

        <div class="flex shrink-0 justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => dialog.close()}>
            {language.t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={invalidName() || noneSelected()}>
            {language.t("project.template.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
